import React, { useState, useEffect } from "react";
import DatePickerCustom from "../../../../component/elements/datePicker";
import TextFieldCustom from "../../../../component/elements/textfieldCustom";
import SelectCustom from "../../../../component/elements/selectCustom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setPayloadAddress,
  setPickupDetails,
} from "../../../../Redux/Slices/customerSlice";
import { FormControl, FormControlLabel, RadioGroup } from "@mui/material";
import RadioCustom from "../../../../component/elements/radioCustom";
import axiosInstance from "../../../../configuration/request";
import { useSnackbar } from "../../../../component/SnackbarContext";
import { timeSlots } from "../../../../constants/placeOrderConst";
import dayjs from "dayjs";
import SectionWrapper from "../../components/sectionWrapper";
import ButtonContainer from "../../components/buttonContainer";

// Boston Timezone formatter
const formatBoston = (date) =>
  new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

function PickupDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showSnackbar = useSnackbar();

  const { loginDetails } = useSelector((state) => state.counter);
  const userId = loginDetails.userId || null;
  const pickupDetails = useSelector((state) => state.customer?.pickupDetails) || {};
  const { selectedShop, orderFlow } = useSelector((state) => state.customer);
  
  const [addresses, setAddresses] = useState([]);
  const [disabledData, setDisabledData] = useState({
    offDays: [],
    backoutDates: []
  });

  const [form, setForm] = useState({
    address: pickupDetails.address || "",
    pickupDate: pickupDetails.pickupDate || "",
    pickupTime: pickupDetails.pickupTime || "",
    deliveryDate: pickupDetails.deliveryDate || "",
    deliveryTime: pickupDetails.deliveryTime || "",
    repeatFrequency: pickupDetails.repeatFrequency || "No Repeat",
    notes: pickupDetails.notes || "",
    laundryNotes: pickupDetails.laundryNotes || "",
  });

  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    const valid =
      form.address &&
      form.pickupDate &&
      form.pickupTime &&
      form.deliveryDate &&
      form.deliveryTime;
    setIsFormValid(valid);
  }, [form]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      let updated = { ...prev, [field]: value };
      if (field === "pickupDate") {
        updated.deliveryDate = "";
        updated.deliveryTime = "";
      }
      syncPickupToRedux(updated);
      return updated;
    });
  };

  const syncPickupToRedux = (updatedForm) => {
    let payload = { ...updatedForm };
    if (updatedForm.address) {
      const selectedAddress = addresses.find((a) => a.value === updatedForm.address);
      payload.addressString = selectedAddress?.label || "";
      dispatch(setPayloadAddress(selectedAddress?.fullAddress || null));
    } else {
      payload.addressString = "";
      dispatch(setPayloadAddress(null));
    }
    dispatch(setPickupDetails(payload));
  };

  /** Fetch Addresses and Date Restrictions */
  useEffect(() => {
    axiosInstance
      .get(`/customer/orders/checkAddress?storeId=${selectedShop?.id || ""}`)
      .then((res) => {
        // Store disabling logic data
        setDisabledData({
          offDays: res?.data?.off_days || [],
          backoutDates: res?.data?.backout_dates || []
        });

        const data = orderFlow === "shop" ? res?.data?.addresses || [] : res?.data || [];
        const formatted = data.map((item) => ({
          label: `${item.house_no || ""} ${item.street_address || ""} ${item.landmark || ""
            } ${item.city || ""} ${item.state || ""} ${item.zipcode || ""}`,
          value: item.id,
          fullAddress: item,
        }));
        setAddresses(formatted);
      })
      .catch(() => showSnackbar("Something went wrong!", "error"));
  }, [selectedShop, orderFlow]);

  /** Function to block off-days and backout dates */
  const shouldDisableDate = (date) => {
    const dayName = date.format("dddd"); // Matches "Saturday", "Sunday"
    const dateString = date.format("YYYY-MM-DD"); // Matches "2026-01-14"

    const isOffDay = disabledData.offDays.includes(dayName);
    const isBackoutDate = disabledData.backoutDates.includes(dateString);

    return isOffDay || isBackoutDate;
  };

  const filterTimeSlots = (selectedDateString) => {
    if (!selectedDateString) return [];
    const today = formatBoston(new Date());
    const todayStr = today.split("/").reverse().join("-");
    if (selectedDateString === todayStr) {
      const nowBoston = new Date().toLocaleTimeString("en-US", {
        timeZone: "America/New_York",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return timeSlots.filter((slot) => {
        const start = slot.value.split(" - ")[0];
        return new Date(`01/01/2020 ${start}`) > new Date(`01/01/2020 ${nowBoston}`);
      });
    }
    return timeSlots;
  };

  const nextHandler = () => {
    if (!isFormValid) {
      showSnackbar("Please fill in all required fields", "error");
      return;
    }
    navigate("/add-to-cart");
  };

  const previousHandler = () => navigate("/customer-home");

  return (
    <div>
      <SectionWrapper title="Enter" subTitle="Pickup Details">
        <div className="flex flex-col px-4 sm:px-6 lg:px-8 gap-6">
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-[#242B33]">Pickup Location*</label>
              <button onClick={() => navigate("/addresses")} className="text-sm text-[#3587B8] font-semibold">
                + Add New Location
              </button>
            </div>
            <SelectCustom
              placeholder="Select your address"
              value={form.address}
              onChange={(e) => handleChange("address", e.target.value)}
              options={addresses}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-[#242B33]">Collection Date*</label>
              <DatePickerCustom
                value={form.pickupDate ? dayjs(form.pickupDate, "YYYY-MM-DD") : null}
                shouldDisableDate={shouldDisableDate}
                onChange={(date) => handleChange("pickupDate", date ? date.format("YYYY-MM-DD") : "")}
                minDate={dayjs()}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-[#242B33]">Collection Time*</label>
              <SelectCustom
                placeholder="Collection Time"
                value={form.pickupTime}
                onChange={(e) => handleChange("pickupTime", e.target.value)}
                options={filterTimeSlots(form.pickupDate)}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-[#242B33]">Expected Date*</label>
              <DatePickerCustom
                value={form.deliveryDate ? dayjs(form.deliveryDate, "YYYY-MM-DD") : null}
                shouldDisableDate={shouldDisableDate}
                onChange={(date) => handleChange("deliveryDate", date ? date.format("YYYY-MM-DD") : "")}
                minDate={form.pickupDate ? dayjs(form.pickupDate, "YYYY-MM-DD").add(1, "day") : dayjs()}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-[#242B33]">Expected Time*</label>
              <SelectCustom
                placeholder="Expected Time"
                value={form.deliveryTime}
                onChange={(e) => handleChange("deliveryTime", e.target.value)}
                options={filterTimeSlots(form.deliveryDate)}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-[#242B33]">Add Notes for Delivery Partners</label>
            <TextFieldCustom
              placeholder="Add Delivery Instructions"
              multiline
              rows={2}
              fullWidth
              value={form.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-[#242B33]">Add Notes for Laundry Shops</label>
            <TextFieldCustom
              placeholder="Add Laundry Instructions"
              multiline
              rows={2}
              fullWidth
              value={form?.laundryNotes}
              onChange={(e) => handleChange("laundryNotes", e.target.value)}
            />
          </div>

          <div>
            <FormControl fullWidth>
              <RadioGroup
                row
                value={form.repeatFrequency}
                onChange={(e) => handleChange("repeatFrequency", e.target.value)}
              >
                {["Repeat Weekly", "Repeat Monthly", "No Repeat"].map((option) => (
                  <FormControlLabel key={option} value={option} control={<RadioCustom />} label={option} />
                ))}
              </RadioGroup>
            </FormControl>
          </div>

          <ButtonContainer handleNext={nextHandler} handlePrevious={previousHandler} />
        </div>
      </SectionWrapper>
    </div>
  );
}

export default PickupDetails;
